import { User } from "../../../../../api/accessControl/User.ts";
import Shell from "../../../../../components/specific/Shell.tsx";
import { useMemo, useState } from "react";
import { getStatsByCurrency, OutstandingBillSummary } from "../../Stats.tsx";
import { InfoTooltip } from "../../../../../components/general/InfoTooltip.tsx";
import { MoneyLabel } from "../../../../../components/general/MoneyLabel.tsx";
import { toDecimalFromMoney } from "../../../../../lib/Money.ts";
import { DEFAULT_CURRENCY } from "../../../../../types/constants.ts";
import { OutstandingBillsTable } from "./OutstandingBillsTable.tsx";
import WideDrawer from "../../../../../components/general/WideDrawer.tsx";
import { DocumentPreview } from "../../../../../views/DocumentDrawer/components/DocumentPreview.tsx";
import type { DocumentContent } from "../../../../../views/DocumentDrawer/components/DocumentPreview.tsx";
import type { OutstandingBillRow } from "./types/outstandingBill.ts";
import { InvoiceFilterBar } from "../invoiceFilters/InvoiceFilterBar.tsx";
import {
    InvoiceDateFieldEnum,
    type InvoiceFilterAccessors,
    type InvoiceFilterState,
} from "../invoiceFilters/types/invoiceFilter.ts";
import {
    collectRelationOptions,
    createEmptyInvoiceFilterState,
    FILTERED_EMPTY_MESSAGE,
    filterInvoiceRows,
    isInvoiceFilterActive,
} from "../invoiceFilters/utils/invoiceFilters.ts";
import type {DocumentDirection} from "../../../../../generated/enums.ts";

export type GetDocumentResponse = {
    file_name: string;
    file_blob: Blob;
    mimetype: string;
};

interface DirectionLabels {
    titleLabel: string;
    totalLabel: string;
    emptyMessage: string;
    relationLabel: string;
    relationPlaceholder: string;
}

const LABELS: Record<
    'purchase_invoice' | 'sales_invoice',
    DirectionLabels
> = {
    purchase_invoice: {
        titleLabel: "Te betalen",
        totalLabel: "Totaal te betalen",
        emptyMessage: "Geen open inkoopfacturen.",
        relationLabel: "Leverancier",
        relationPlaceholder: "Alle leveranciers",
    },
    sales_invoice: {
        titleLabel: "Te ontvangen",
        totalLabel: "Totaal te ontvangen",
        emptyMessage: "Geen open verkoopfacturen.",
        relationLabel: "Klant",
        relationPlaceholder: "Alle klanten",
    },
};

const UNKNOWN_DIRECTION_LABELS: DirectionLabels = {
    titleLabel: "",
    totalLabel: "",
    emptyMessage: "",
    relationLabel: "Relatie",
    relationPlaceholder: "Alle relaties",
};

const DUE_DATE_PERIOD_LABEL = "Periode - vervaldatum";

const BILL_FILTER_ACCESSORS: InvoiceFilterAccessors<OutstandingBillRow> = {
    getRelation: (bill) => bill.relation,
    getDate: (bill) => bill.normalized_due_date,
};

export interface OutstandingBillsProps {
    currentUser: User;
    direction: DocumentDirection;
    stats: OutstandingBillSummary | null;
    bills: OutstandingBillRow[];
    isLoading: boolean;
    onRowClick: (bill: OutstandingBillRow) => Promise<GetDocumentResponse | null>;
    onReportIncorrect: (bill: OutstandingBillRow) => Promise<void>;
}

export function OutstandingBills({
    currentUser,
    direction,
    stats,
    bills,
    isLoading,
    onRowClick,
    onReportIncorrect,
}: OutstandingBillsProps) {
    const [selectedBill, setSelectedBill] = useState<OutstandingBillRow | null>(
        null
    );
    const [previewDoc, setPreviewDoc] = useState<DocumentContent | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [filters, setFilters] = useState<InvoiceFilterState>(() =>
        createEmptyInvoiceFilterState(InvoiceDateFieldEnum.DUE_DATE)
    );

    const relationOptions = useMemo(
        () => collectRelationOptions(bills, (bill) => bill.relation),
        [bills]
    );
    const visibleBills = useMemo(
        () => filterInvoiceRows(bills, filters, BILL_FILTER_ACCESSORS),
        [bills, filters]
    );
    const isFiltered = isInvoiceFilterActive(filters);

    const labels =
        direction === 'purchase_invoice' ||
        direction === 'sales_invoice'
            ? LABELS[direction]
            : UNKNOWN_DIRECTION_LABELS;

    const statsItems =
        direction === 'purchase_invoice'
            ? stats?.purchases
            : stats?.sales;
    const statsByCurrency = getStatsByCurrency(statsItems, DEFAULT_CURRENCY);
    const totalBillCount =
        direction === 'purchase_invoice'
            ? (stats?.total_purchase_bill_count ?? 0)
            : (stats?.total_sales_bill_count ?? 0);

    const handleRowClick = async (bill: OutstandingBillRow) => {
        setSelectedBill(bill);
        setIsDrawerOpen(true);
        setPreviewDoc(null);
        const data = await onRowClick(bill);
        if (data) {
            setPreviewDoc({
                file_name: data.file_name,
                file_blob: data.file_blob,
                mimetype: data.mimetype,
            });
        }
    };


    return (
        <>
            <Shell currentUser={currentUser} isLoading={isLoading}>
                <div className="mb-8">
                    <h1 className="weight-600 text-3xl">{labels.titleLabel}</h1>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4 mb-8">
                    <div className="p-6 bg-white rounded-lg shadow-sm border border-gray-100 md:col-span-1">
                        <dd className="text-xl font-semibold tracking-tight text-gray-900">
                            <MoneyLabel
                                value={toDecimalFromMoney(statsByCurrency.value)}
                                showCurrency={true}
                                currency={statsByCurrency.currency}
                            />
                        </dd>
                        <dt className="mt-1 text-base font-medium text-gray-700 flex items-center gap-1">
                            {labels.totalLabel}
                            <InfoTooltip
                                position="top"
                                text="Dit betreft alleen de som van facturen in Euro."
                            />
                        </dt>
                    </div>
                    <div className="p-6 bg-white rounded-lg shadow-sm border border-gray-100 md:col-span-1">
                        <dd className="text-xl font-semibold tracking-tight text-gray-900">
                            {totalBillCount}
                        </dd>
                        <dt className="mt-1 text-base font-medium text-gray-700">
                            Aantal open documenten
                        </dt>
                    </div>
                </div>
                <InvoiceFilterBar
                    value={filters}
                    onChange={setFilters}
                    relationOptions={relationOptions}
                    relationLabel={labels.relationLabel}
                    relationPlaceholder={labels.relationPlaceholder}
                    dateLabel={DUE_DATE_PERIOD_LABEL}
                />
                <div className="overflow-hidden">
                    <OutstandingBillsTable
                        bills={visibleBills}
                        totalCount={totalBillCount}
                        isFiltered={isFiltered}
                        onRowClick={handleRowClick}
                        onReportDocumentIncorrect={onReportIncorrect}
                        emptyMessage={isFiltered ? FILTERED_EMPTY_MESSAGE : labels.emptyMessage}
                    />
                </div>
                <WideDrawer
                    open={isDrawerOpen}
                    updateOpenState={(open: boolean) => {
                        if (!open) {
                            setSelectedBill(null);
                            setPreviewDoc(null);
                        }
                        setIsDrawerOpen(open);
                    }}
                    title={selectedBill?.file_name ?? "Document"}
                >
                    {previewDoc && <DocumentPreview document={previewDoc} />}
                </WideDrawer>
            </Shell>
        </>
    );
}
